import React from "react";
import { Input, Select, Button, Spin, Tag, Tooltip, Alert, Checkbox, Radio } from "antd";
import {
  IdcardOutlined,
  BorderOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  AppstoreAddOutlined,
  DollarCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import FormField from "../FormField";
import {
  BASEMENT_OPTIONS,
  DIRECTION_OPTIONS,
  PLAN_TYPE_OPTIONS,
  PROJECT_COST_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  STAIRCASE_OPTIONS,
  getRoomFields,
  isBhkProjectType,
} from "../../constants";

const { Option } = Select;

const SectionHead = ({ icon, title, note }) => (
  <div className="mmh-section-head">
    <span className="mmh-icon-chip">{icon}</span>
    <div className="mmh-section-head-text">
      <div className="mmh-section-title">{title}</div>
      {note && <div className="mmh-section-note">{note}</div>}
    </div>
  </div>
);

const DetailsStep = ({
  form,
  errors,
  masters,
  setField,
  handleInput,
  onProjectTypeChange,
  customFields,
  autoFillState,
  pincodeState,
  suggestedName,
  onLookupProjectCode,
  onLookupPincode,
  onAddCustomField,
  onUpdateCustomField,
  onRemoveCustomField,
  onApplySuggestedName,
}) => {
  const nameDiffers = Boolean(suggestedName) && suggestedName !== form.projectName;

  return (
    <>
      {/* PRODUCT IDENTITY ------------------------------------------------ */}
      <section className="mmh-section">
        <SectionHead
          icon={<IdcardOutlined />}
          title="Product Identity"
          note="Enter a project code to pull dimensions automatically, or fill everything by hand"
        />
        <div className="mmh-grid-2">
          <FormField label="Project Code" required error={errors.projectCode} hint="Press Enter or tab out to look it up">
            <Input
              name="projectCode"
              value={form.projectCode}
              placeholder="e.g. PRJ202405210032"
              onChange={handleInput}
              onBlur={onLookupProjectCode}
              onPressEnter={onLookupProjectCode}
              suffix={autoFillState.loading ? <Spin size="small" /> : null}
              allowClear
            />
          </FormField>

          <FormField label="Product Name" required error={errors.projectName}>
            <Input
              name="projectName"
              value={form.projectName}
              placeholder="Generated from the details below, or type your own"
              onChange={handleInput}
            />
          </FormField>
        </div>

        {autoFillState.code && (
          <Tag color="gold" className="mmh-autofill-tag">
            <ThunderboltOutlined /> Auto-filled from {autoFillState.code} — you can still edit any field
          </Tag>
        )}

        {nameDiffers && (
          <Alert
            type="info"
            className="mmh-name-suggestion"
            showIcon
            message={<span>Suggested name: <strong>{suggestedName}</strong></span>}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={onApplySuggestedName}>
                Use this
              </Button>
            }
          />
        )}
      </section>

      {/* DIMENSIONS & STRUCTURE ----------------------------------------- */}
      <section className="mmh-section">
        <SectionHead
          icon={<BorderOutlined />}
          title="Dimensions & Structure"
          note="Plot area and build-up area are calculated from width, length and floors"
        />
        <div className="mmh-grid-2">
          <FormField label="Width (ft)" required error={errors.width}>
            <Input name="width" type="number" min={0} value={form.width} placeholder="Width" onChange={handleInput} />
          </FormField>

          <FormField label="Length (ft)" required error={errors.length}>
            <Input name="length" type="number" min={0} value={form.length} placeholder="Length" onChange={handleInput} />
          </FormField>

          <FormField label="No of Floor" required error={errors.floor}>
            <Input name="floor" type="number" min={0} value={form.floor} placeholder="No of Floor" onChange={handleInput} />
          </FormField>

          <FormField label="Plot Area Of Work (sq ft)" hint="width x length">
            <Tooltip title="Calculated from width and length">
              <Input value={form.plotArea} placeholder="Auto-calculated" readOnly className="mmh-derived-input" />
            </Tooltip>
          </FormField>

          <FormField label="Build up area (sq ft)" hint="width x length x floors">
            <Tooltip title="Calculated from width, length and floors">
              <Input value={form.buildUpArea} placeholder="Auto-calculated" readOnly className="mmh-derived-input" />
            </Tooltip>
          </FormField>

          <FormField label="Staircase Type">
            <Select
              value={form.staircaseType || undefined}
              placeholder="Staircase Type"
              onChange={(v) => setField("staircaseType", v ?? "")}
              allowClear
            >
              {STAIRCASE_OPTIONS.map((o) => (
                <Option value={o.value} key={o.value}>{o.label}</Option>
              ))}
            </Select>
          </FormField>

          <FormField label="Vastu Priority">
            <Input name="vastuPriority" value={form.vastuPriority} placeholder="Vastu Priority" onChange={handleInput} />
          </FormField>

          <FormField label="Select Basement">
            <Select
              value={form.basement || undefined}
              placeholder="Select Basement"
              onChange={(v) => setField("basement", v ?? "")}
              allowClear
            >
              {BASEMENT_OPTIONS.map((o) => (
                <Option value={o} key={o}>{o}</Option>
              ))}
            </Select>
          </FormField>

          <FormField label="Direction" required error={errors.direction}>
            <Select
              value={form.direction || undefined}
              placeholder="Select Direction"
              onChange={(v) => setField("direction", v ?? "")}
              allowClear
            >
              {DIRECTION_OPTIONS.map((o) => (
                <Option value={o.value} key={o.value}>{o.label}</Option>
              ))}
            </Select>
          </FormField>
        </div>
      </section>

      {/* LOCATION & CATEGORY -------------------------------------------- */}
      <section className="mmh-section">
        <SectionHead
          icon={<EnvironmentOutlined />}
          title="Location & Category"
          note="Client location fills itself from the pincode"
        />
        <div className="mmh-grid-2">
          <FormField label="Pincode of Site" required error={errors.pincode} hint="6 digits — press Enter to resolve">
            <Input
              name="pincode"
              value={form.pincode}
              placeholder="e.g. 452001"
              maxLength={6}
              onChange={handleInput}
              onBlur={onLookupPincode}
              onPressEnter={onLookupPincode}
              suffix={pincodeState.loading ? <Spin size="small" /> : null}
            />
          </FormField>

          <FormField label="Client location" required error={errors.clientLocation}>
            <Input
              name="clientLocation"
              value={form.clientLocation}
              placeholder="City [State]"
              onChange={handleInput}
            />
          </FormField>

          <FormField label="Select Category" required error={errors.category}>
            <Select
              value={form.category ? String(form.category) : undefined}
              placeholder="Select Category"
              onChange={(v) => setField("category", v ?? "")}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {masters.categories.map((c) => (
                <Option value={String(c.id)} key={c.id}>{c.name}</Option>
              ))}
            </Select>
          </FormField>

          <FormField label="Select category Type" required error={errors.categoryType}>
            <Select
              value={form.categoryType || undefined}
              placeholder="Select category Type"
              onChange={(v) => setField("categoryType", v ?? "")}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {masters.categoryTypes.map((t) => (
                <Option value={t} key={t}>{t}</Option>
              ))}
            </Select>
          </FormField>
        </div>
      </section>

      {/* PROJECT TYPE ---------------------------------------------------- */}
      <section className="mmh-section">
        <SectionHead
          icon={<HomeOutlined />}
          title="Project type"
          note={
            isBhkProjectType(form.projectType)
              ? "Enter how many units of each BHK size the project has"
              : "Enter how many of each room the house has"
          }
        />

        <Radio.Group
          className="mmh-type-radio-row"
          value={Number(form.projectType) || undefined}
          onChange={(e) => onProjectTypeChange(e.target.value)}
        >
          {PROJECT_TYPE_OPTIONS.map((o) => (
            <Radio key={o.value} value={o.value}>{o.label}</Radio>
          ))}
        </Radio.Group>

        {errors.projectType && <div className="mmh-field-error">{errors.projectType}</div>}

        <div className="mmh-grid-2 mmh-room-grid">
          {getRoomFields(form.projectType).map((f) => (
            <FormField label={f.label} key={f.name}>
              <Input
                name={f.name}
                type="number"
                min={0}
                value={form[f.name]}
                placeholder={f.placeholder}
                onChange={handleInput}
              />
            </FormField>
          ))}
        </div>

        <div className="mmh-parking-row">
          <Checkbox
            checked={Boolean(Number(form.parking))}
            onChange={(e) => setField("parking", e.target.checked ? 1 : 0)}
          >
            Parking
          </Checkbox>
        </div>
      </section>

      {/* CUSTOM FIELDS --------------------------------------------------- */}
      <section className="mmh-section">
        <SectionHead
          icon={<AppstoreAddOutlined />}
          title="Custom Fields"
          note="Anything the fixed fields above don't cover — e.g. Pooja Room, Wash Area, Shops"
        />
        <div className="mmh-custom-fields-list">
          {customFields.map((f, index) => (
            <div className="mmh-custom-field-row" key={f.id}>
              <FormField label={index === 0 ? "Custome Field" : null}>
                <Input
                  placeholder="Custome Field"
                  value={f.name}
                  onChange={(e) => onUpdateCustomField(f.id, "name", e.target.value)}
                />
              </FormField>
              <FormField label={index === 0 ? "No of Custome Field" : null}>
                <Input
                  type="number"
                  min={0}
                  placeholder="No of Custome Field"
                  value={f.value}
                  onChange={(e) => onUpdateCustomField(f.id, "value", e.target.value)}
                />
              </FormField>
              <Tooltip title="Remove this field">
                <button
                  type="button"
                  className="mmh-cf-remove"
                  aria-label="Remove this custom field"
                  onClick={() => onRemoveCustomField(f.id)}
                >
                  <DeleteOutlined />
                </button>
              </Tooltip>
            </div>
          ))}
        </div>
        <Button className="mmh-add-more-btn" icon={<PlusOutlined />} onClick={onAddCustomField}>
          Add More field
        </Button>
      </section>

      {/* COST, PLAN & CLIENT --------------------------------------------- */}
      <section className="mmh-section">
        <SectionHead icon={<DollarCircleOutlined />} title="Cost, Plan & Client" />
        <div className="mmh-grid-2">
          <FormField label="Select Project Cost" required error={errors.projectCost}>
            <Select
              value={form.projectCost || undefined}
              placeholder="Select Project Cost"
              onChange={(v) => setField("projectCost", v ?? "")}
              allowClear
            >
              {PROJECT_COST_OPTIONS.map((o) => (
                <Option value={o} key={o}>{o}</Option>
              ))}
            </Select>
          </FormField>

          <FormField label="Select Plan Type" required error={errors.planType}>
            <Select
              value={form.planType || undefined}
              placeholder="Select Plan Type"
              onChange={(v) => setField("planType", v ?? "")}
              allowClear
            >
              {PLAN_TYPE_OPTIONS.map((o) => (
                <Option value={o.value} key={o.value}>{o.label}</Option>
              ))}
            </Select>
          </FormField>

          <FormField label="Product Code" required error={errors.productCode}>
            <Input name="productCode" value={form.productCode} placeholder="product code" onChange={handleInput} />
          </FormField>

          <FormField label="Client Name" required error={errors.clientName}>
            <Input name="clientName" value={form.clientName} placeholder="Client Name" onChange={handleInput} />
          </FormField>

          <FormField label="Add Link" error={errors.addLink} className="mmh-span-2">
            <Input name="addLink" value={form.addLink} placeholder="Add Link" onChange={handleInput} />
          </FormField>

          <FormField label="Description" className="mmh-span-2">
            <Input.TextArea
              name="description"
              value={form.description}
              onChange={handleInput}
              rows={3}
              placeholder="Description"
              showCount
              maxLength={2000}
            />
          </FormField>
        </div>
      </section>
    </>
  );
};

export default DetailsStep;
